import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User as SupabaseUser, Session } from "@supabase/supabase-js";
import { claimGuestRecords } from "@/services/guestClaimService";

export type UserRole = "customer" | "courier" | "admin" | "support" | "affiliate" | "partner";

const PASSWORD_RECOVERY_FLAG = "ec_password_recovery_active";

export interface AppUser {
  id: string;
  name: string;
  email: string;
  phone: string;
  role: UserRole;
  user_metadata?: Record<string, any>;
}

interface AuthContextType {
  user: AppUser | null;
  session: Session | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (credentials: { email: string; password: string }) => Promise<AppUser>;
  signup: (credentials: { 
    email: string; 
    password: string; 
    name: string; 
    role: UserRole;
    emailRedirectPath?: string;
    metadata?: {
      first_name?: string;
      last_name?: string;
      country?: string;
      preferred_language?: string;
      phone?: string;
    };
  }) => Promise<SignupResult>;
  logout: () => Promise<void>;
}

/**
 * Supabase GoTrue has email-enumeration protection enabled (email
 * confirmations are ON / mailer_autoconfirm=false). Signing up with an
 * address that already exists returns HTTP 200 with an obfuscated user
 * and an EMPTY `identities` array — never an error. Without special
 * handling a duplicate signup looks like a silent success.
 *
 * We surface a single safe internal status. It carries NO account data:
 * only the fact that the submitted address is already registered. The
 * obfuscated user object returned by the provider is discarded here and
 * never reaches the UI or app state.
 */
export type SignupStatus = "OK" | "EMAIL_ALREADY_REGISTERED";

export interface SignupResult {
  status: SignupStatus;
  /** @deprecated use `status === "EMAIL_ALREADY_REGISTERED"` */
  alreadyRegistered: boolean;
}

/** Provider error codes/messages that mean "this email already exists". */
export function isDuplicateSignupError(
  error: { message?: string; code?: string; status?: number } | null,
): boolean {
  if (!error) return false;
  const code = (error.code ?? "").toLowerCase();
  if (code === "user_already_exists" || code === "email_exists") return true;
  const msg = (error.message ?? "").toLowerCase();
  return msg.includes("already registered") || msg.includes("already been registered") || msg.includes("user already exists");
}

const isDuplicateEmailError = isDuplicateSignupError;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

function buildFallbackUser(supaUser: SupabaseUser): AppUser {
  // SECURITY: Never trust user_metadata for role — always default to 'customer'
  // Roles are ONLY authoritative from the user_roles table (fetched in fetchAppUser)
  return {
    id: supaUser.id,
    name: supaUser.user_metadata?.full_name || "",
    email: supaUser.email || "",
    phone: supaUser.user_metadata?.whatsapp || "",
    role: "customer",
    user_metadata: supaUser.user_metadata,
  };
}

async function fetchAppUser(supaUser: SupabaseUser): Promise<AppUser> {
  const fallback = buildFallbackUser(supaUser);

  try {
    const [profileRes, rolesRes] = await Promise.all([
      supabase
        .from("profiles")
        .select("full_name, email, role, phone, welcome_email_sent")
        .eq("id", supaUser.id)
        .maybeSingle(),
      supabase
        .from("user_roles")
        .select("role")
        .eq("user_id", supaUser.id),
    ]);

    const profile = profileRes.data;
    const userRoles = rolesRes.data;

    // If email is confirmed and welcome email hasn't been sent yet,
    // touch the profile to trigger the welcome email via DB trigger
    if (
      profile &&
      !(profile as any).welcome_email_sent &&
      supaUser.email_confirmed_at
    ) {
      // Determine role for welcome email
      const welcomeRole: "customer" | "courier" =
        userRoles?.some((r: any) => r.role === "courier") ? "courier" : "customer";

      // Touch profile to trigger the welcome email via DB trigger (on_profile_send_welcome)
      supabase
        .from("profiles")
        .update({ updated_at: new Date().toISOString() })
        .eq("id", supaUser.id)
        .then(() => {
          console.log("Welcome email trigger touched for", supaUser.id);
        });
    }

    let role: UserRole = "customer";
    if (userRoles && userRoles.length > 0) {
      const roleValues = userRoles.map((r: any) => r.role as string);
      if (roleValues.includes("admin")) role = "admin";
      else if (roleValues.includes("support")) role = "support";
      else if (roleValues.includes("courier")) role = "courier";
      else if (roleValues.includes("affiliate")) role = "affiliate";
      else if (roleValues.includes("partner")) role = "partner";
      else role = "customer";
    }
    // No fallback to profiles.role — user_roles table is the single source of truth
    
    return {
      id: supaUser.id,
      name: profile?.full_name || fallback.name,
      email: profile?.email || fallback.email,
      phone: profile?.phone || fallback.phone,
      role,
      user_metadata: supaUser.user_metadata,
    };
  } catch {
    return fallback;
  }
}

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<AppUser | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    // 1. Restore session from storage FIRST
    supabase.auth.getSession().then(async ({ data: { session: s } }) => {
      if (!mounted) return;
      setSession(s);
      if (s?.user) {
        const appUser = await fetchAppUser(s.user);
        if (mounted) setUser(appUser);
      }
      if (mounted) setLoading(false);
    });

    // 2. Listen for subsequent auth changes (sign in, sign out, token refresh)
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, newSession) => {
        if (!mounted) return;
        const isResetPasswordRoute =
          typeof window !== "undefined" && window.location.pathname === "/reset-password";
        if (event === "PASSWORD_RECOVERY" || (event === "SIGNED_IN" && isResetPasswordRoute)) {
          try {
            sessionStorage.setItem(PASSWORD_RECOVERY_FLAG, "true");
          } catch {
            // Session storage may be unavailable in private browsing.
          }
        }
        // Mark that the user has performed an explicit sign-in in this browser.
        // The PWA install banner uses this to avoid showing on first app load
        // for returning sessions — it only appears after a fresh login event.
        if (event === "SIGNED_IN" && !isResetPasswordRoute) {
          try {
            localStorage.setItem("pwa-install-banner-eligible", "true");
          } catch {
            // ignore
          }
        }
        if (event === "SIGNED_OUT") {
          try {
            localStorage.removeItem("pwa-install-banner-eligible");
          } catch {
            // ignore
          }
        }
        setSession(newSession);
        if (newSession?.user) {
          // Use setTimeout to avoid calling Supabase inside the callback (prevents deadlock)
          setTimeout(async () => {
            if (!mounted) return;
            const appUser = await fetchAppUser(newSession.user!);
            if (mounted) {
              setUser(appUser);
              setLoading(false);
            }
            // Auto-claim guest records (bookings, quotes, shipment
            // requests, conversations) matching this user's e-mail.
            // Idempotent; failures are surfaced and stay retryable —
            // never silently swallowed.
            const email = newSession.user?.email;
            if (email) {
              try {
                await claimGuestRecords(newSession.user!.id, email);
              } catch (err) {
                console.error("[auth] guest record hand-off failed", err);
              }
            }
          }, 0);
        } else {
          setUser(null);
          setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const login = async (credentials: { email: string; password: string }): Promise<AppUser> => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email: credentials.email,
      password: credentials.password,
    });
    if (error) throw error;
    if (!data.user) throw new Error("Login failed");
    
    const appUser = await fetchAppUser(data.user);
    setUser(appUser);
    setSession(data.session);
    return appUser;
  };

  const signup = async (credentials: { 
    email: string; 
    password: string; 
    name: string; 
    role: UserRole;
    emailRedirectPath?: string;
    metadata?: {
      first_name?: string;
      last_name?: string;
      country?: string;
      preferred_language?: string;
      phone?: string;
    };
  }): Promise<SignupResult> => {
    const { data, error } = await supabase.auth.signUp({
      email: credentials.email,
      password: credentials.password,
      options: {
        emailRedirectTo: typeof window !== "undefined" ? `${window.location.origin}${credentials.emailRedirectPath ?? ""}` : "",
        data: {
          full_name: credentials.name,
          role: credentials.role,
          ...credentials.metadata,
        },
      },
    });
    if (error) {
      // Provider may return an explicit duplicate error when enumeration
      // protection is off. Map it to the same safe status.
      if (isDuplicateEmailError(error)) {
        return { status: "EMAIL_ALREADY_REGISTERED", alreadyRegistered: true };
      }
      throw error;
    }
    // Enumeration-protected duplicate: 200 OK, user present, no identities.
    const alreadyRegistered =
      !!data?.user && (data.user.identities?.length ?? 0) === 0;
    return {
      status: alreadyRegistered ? "EMAIL_ALREADY_REGISTERED" : "OK",
      alreadyRegistered,
    };
  };

  const logout = async () => {
    // Detach this device's push subscription BEFORE the session is gone,
    // otherwise the row stays behind and the account keeps pushing here.
    try {
      const { releaseDevicePushSubscription } = await import("@/hooks/usePushNotifications");
      await releaseDevicePushSubscription();
    } catch {
      // Push release is best-effort; never block sign-out.
    }
    // Saved checkout belongs to the signed-in person; never leave it behind.
    try { (await import("@/lib/pendingCheckout")).clearPendingCheckout(); } catch { /* best-effort */ }
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
  };


  return (
    <AuthContext.Provider value={{ user, session, isAuthenticated: !!session, loading, login, signup, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
